import { useQuery } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { Alert, Badge, Button, Card, Field, Select, Spinner } from '../../components/ui';
import { type MessageKey, t } from '../../i18n';
import { api } from '../../lib/api';
import { useMe } from '../../lib/auth';
import { errorMessage } from '../../lib/errors';
import { can } from '../../lib/permissions';
import { formatDay, PROJECT_STATUSES, type ProjectListItem, type ProjectScope, projectStatusName, projectStatusTone, todayIso } from './data';
import { ProjectsNav } from './ProjectsNav';
import { priorityName } from './requests-data';

const FILTERS = ['activos', ...PROJECT_STATUSES, 'todos'] as const;

/** Proyectos (guía 7.2): los míos (responsable o con tareas) y, con permiso, todos los de mis organizaciones. */
export function ProjectsPage() {
  const me = useMe();
  const staff = can(me, 'proyectos.ver') || can(me, 'proyectos.gestionar');
  const scopes: ProjectScope[] = ['mios', ...(staff ? (['todos'] as const) : [])];
  const [params, setParams] = useSearchParams();
  const requested = params.get('alcance') as ProjectScope | null;
  const scope: ProjectScope = requested && scopes.includes(requested) ? requested : staff ? 'todos' : 'mios';
  const filter = (FILTERS as readonly string[]).includes(params.get('estado') ?? '') ? params.get('estado')! : 'activos';
  const search = params.get('buscar') ?? '';
  const [draft, setDraft] = useState(search);

  const query = new URLSearchParams({ alcance: scope });
  if (filter !== 'todos') query.set('estado', filter);
  if (search) query.set('buscar', search);
  const projects = useQuery({
    queryKey: ['proyectos', 'lista', scope, filter, search],
    queryFn: () => api<ProjectListItem[]>(`/api/proyectos?${query}`),
    placeholderData: (prev) => prev,
  });

  const update = (changes: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(changes)) {
      if (v) next.set(k, v);
      else next.delete(k);
    }
    setParams(next, { replace: true });
  };
  const submitSearch = (e: FormEvent) => {
    e.preventDefault();
    update({ buscar: draft.trim() || null });
  };
  const today = todayIso();

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-tinta">{t('pr.titulo')}</h1>
          <p className="max-w-2xl text-sm text-slate-600">{t('pr.descripcion')}</p>
        </div>
        {can(me, 'proyectos.gestionar') && (
          <Link to="/proyectos/nuevo" className="rounded bg-marca px-4 py-2 text-sm font-medium text-white hover:bg-marca-claro">
            {t('pr.nuevo')}
          </Link>
        )}
      </div>
      <ProjectsNav />

      <div className="flex flex-wrap items-end gap-3">
        {scopes.length > 1 && (
          <div className="w-44">
            <Select id="pr-alcance" label={t('pr.alcance')} value={scope} onChange={(e) => update({ alcance: e.target.value })}>
              {scopes.map((s) => (
                <option key={s} value={s}>
                  {t(`pr.alcance_${s}` as MessageKey)}
                </option>
              ))}
            </Select>
          </div>
        )}
        <div className="w-48">
          <Select id="pr-estado" label={t('pr.estado')} value={filter} onChange={(e) => update({ estado: e.target.value === 'activos' ? null : e.target.value })}>
            {FILTERS.map((f) => (
              <option key={f} value={f}>
                {f === 'activos' || f === 'todos' ? t(`pr.filtro_${f}` as MessageKey) : projectStatusName(f)}
              </option>
            ))}
          </Select>
        </div>
        <form onSubmit={submitSearch} className="flex items-end gap-2" role="search">
          <div className="w-72">
            <Field id="pr-buscar" type="search" label={t('pr.buscar')} placeholder={t('pr.buscar_ayuda')} value={draft} onChange={(e) => setDraft(e.target.value)} />
          </div>
          <Button type="submit" variant="secondary">
            {t('pr.buscar')}
          </Button>
        </form>
      </div>

      <Card className="p-0">
        {!projects.data ? (
          <div className="p-5">{projects.error ? <Alert>{errorMessage(projects.error)}</Alert> : <Spinner label={t('comun.cargando')} />}</div>
        ) : projects.data.length === 0 ? (
          <p className="p-5 text-sm text-slate-500">{search ? t('pr.sin_resultados') : t('pr.vacio')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-slate-200 text-xs text-slate-500">
                <tr>
                  <th className="px-4 py-2 font-medium">{t('pr.col_proyecto')}</th>
                  <th className="px-4 py-2 font-medium">{t('pr.col_estado')}</th>
                  <th className="px-4 py-2 font-medium">{t('pr.col_responsable')}</th>
                  <th className="px-4 py-2 font-medium">{t('pr.col_avance')}</th>
                  <th className="px-4 py-2 font-medium">{t('pr.col_vence')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {projects.data.map((p) => (
                  <tr key={p.id} className="hover:bg-slate-50">
                    <td className="px-4 py-2">
                      <Link to={`/proyectos/${p.number}`} className="font-medium text-slate-900 hover:underline">
                        <span className="mr-2 font-mono text-xs text-slate-500">{p.code}</span>
                        {p.name}
                      </Link>
                      <span className="block text-xs text-slate-500">
                        {[p.organization, p.projectType, t('pr.prioridad_corta', { prioridad: priorityName(p.priority) })].filter(Boolean).join(' · ')}
                      </span>
                    </td>
                    <td className="px-4 py-2">
                      <Badge tone={projectStatusTone(p.status)}>{projectStatusName(p.status)}</Badge>
                    </td>
                    <td className="px-4 py-2 text-slate-700">{p.owner ?? <span className="text-slate-400">{t('pr.sin_responsable')}</span>}</td>
                    <td className="px-4 py-2">
                      <Progress done={p.progress.done} total={p.progress.total} />
                    </td>
                    <td className={`px-4 py-2 ${p.dueDate && p.dueDate < today && p.status !== 'terminado' && p.status !== 'cancelado' ? 'font-medium text-red-700' : 'text-slate-600'}`}>
                      {p.dueDate ? formatDay(p.dueDate) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

/** Avance: tareas terminadas sobre las que no se cancelaron. */
export function Progress({ done, total }: { done: number; total: number }) {
  const pct = total === 0 ? 0 : Math.round((done / total) * 100);
  return (
    <div className="flex items-center gap-2">
      <div
        role="progressbar"
        aria-label={t('pr.avance')}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={pct}
        className="h-2 w-24 overflow-hidden rounded bg-slate-100"
      >
        <div className="h-full bg-green-600" style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-slate-600">{t('pr.avance_texto', { hechas: done, total })}</span>
    </div>
  );
}
